import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {Alert, Badge, Button, Card, Col, Container, Row, Spinner, Tab, Tabs} from 'react-bootstrap';
import {useTranslation} from 'react-i18next';
import {Award, Calendar, ExternalLink, Facebook, Instagram, MapPin, Music, Twitter} from 'react-feather';
import {getUserByUsername} from '../services/api';
import {UserProfile} from "../types/UserProfile";
import {getBasePhotoUrl} from "../services/api";
import { Modal } from 'react-bootstrap';

const DjProfile: React.FC = () => {
    const {username} = useParams<{ username: string }>();
    const {t} = useTranslation();
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [events, setEvents] = useState<Event[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('events');
    const [showModal, setShowModal] = useState(false);
    const [coverPhotoShowModal, setCoverPhotoShowModal] = useState(false);

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                setLoading(true);
                setError('');

                if (!username) {
                    setError('No username provided');
                    setLoading(false);
                    return;
                }

                // Fetch user profile
                const userResponse = await getUserByUsername(username);
                // const userResponse = await getUserByUsername(username);
                setProfile(userResponse.data);
                console.log(userResponse.data)


            } catch (error: any) {
                setError(error.response?.data?.error || 'Failed to load DJ profile');
                console.error('Error fetching DJ profile:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProfileData();
    }, [username]);

    const renderSocialIcon = (platform: string) => {
        switch (platform.toLowerCase()) {
            case 'facebook':
                return <Facebook size={20}/>;
            case 'twitter':
                return <Twitter size={20}/>;
            case 'instagram':
                return <Instagram size={20}/>;
            default:
                return <ExternalLink size={20}/>;
        }
    };

    if (loading) {
        return (
            <Container className="py-5 text-center">
                <Spinner animation="border" variant="primary"/>
                <p className="mt-3">{t('djProfile.loading')}</p>
            </Container>
        );
    }

    if (error || !profile) {
        return (
            <Container className="py-5">
                <Alert variant="danger">
                    <Alert.Heading>{t('djProfile.error.title')}</Alert.Heading>
                    <p>{error || t('djProfile.error.message')}</p>
                    <Link to="/" className="text-decoration-none">
                        <Button variant="outline-danger">
                            {t('djProfile.error.goBack')}
                        </Button>
                    </Link>

                </Alert>
            </Container>
        );
    }

    return (
        <div className="dj-profile">
            {/* Cover Photo */}
            <div>
                <div
                    className="cover-photo custom-background-color"
                    style={{
                        height: '400px',
                        backgroundImage: profile.cover_photo ? `url(${getBasePhotoUrl() + profile.cover_photo})` : 'linear-gradient(135deg, #1F3045 0%, #4F46E5 100%)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        cursor: 'pointer'
                    }}
                    onClick={() => setCoverPhotoShowModal(true)}
                />

                {/* Cover Photo Modal */}
                <Modal
                    show={coverPhotoShowModal}
                    onHide={() => setCoverPhotoShowModal(false)}
                    centered
                    backdrop="static"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)', // dark background overlay
                    }}
                >
                    <div
                        style={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            padding: '16px',
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            overflow: 'auto',
                            position: 'relative',
                        }}
                    >
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setCoverPhotoShowModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '24px',
                                    cursor: 'pointer',
                                }}
                            >
                                &times;
                            </button>
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <img
                                src={getBasePhotoUrl() + profile.cover_photo}
                                alt={profile.name}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    height: 'auto',
                                    width: 'auto',
                                    borderRadius: '8px',
                                }}
                            />
                        </div>
                    </div>
                </Modal>

            </div>


            <Container>
                {/* Profile Header */}
                <Card className="border-0 shadow-sm mb-4" style={{marginTop: '-75px'}}>
                    <Card.Body>
                        <Row>
                            <Col md={3} className="text-center text-md-start">
                                <div
                                    className="profile-photo rounded-circle mx-auto"
                                    style={{
                                        width: '150px',
                                        height: '150px',
                                        backgroundImage: profile.profile_photo
                                            ? `url(${getBasePhotoUrl() + profile.profile_photo})`
                                            : 'linear-gradient(135deg, #6C63FF 0%, #4F46E5 100%)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        marginTop: '-75px',
                                        border: '5px solid white',
                                        cursor: 'pointer'
                                    }}
                                    onClick={() => setShowModal(true)}
                                />

                                {/* Avatar Modal */}
                                <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                                    <Modal.Header closeButton>
                                        <Modal.Title>{profile.name}</Modal.Title>
                                    </Modal.Header>
                                    <Modal.Body className="text-center">
                                        <img
                                            src={getBasePhotoUrl() + profile.profile_photo}
                                            alt={profile.name}
                                            style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                                        />
                                    </Modal.Body>
                                </Modal>
                            </Col>
                            <Col md={9} className="mt-4 mt-md-0">
                                <div className="d-flex flex-wrap justify-content-between align-items-start">
                                    <div>
                                        <h1 className="h2 mb-1 d-flex align-items-center">
                                            {profile.name}
                                            {profile.is_verified && (
                                                <Badge bg="primary" className="ms-2">
                                                    <Award size={14} className="me-1"/>
                                                    {t('djProfile.verified')}
                                                </Badge>
                                            )}
                                        </h1>
                                        <p className="text-muted mb-3">@{profile.username}</p>
                                    </div>
                                    <a href={`mailto:${profile.contact_email}`} style={{ textDecoration: 'none' }}>
                                        <Button
                                            type="button"
                                            style={{
                                                backgroundColor: '#795EFF',
                                                borderColor: '#795EFF',
                                                color: 'white'
                                            }}
                                        >
                                            {t('djProfile.contact')}
                                        </Button>
                                    </a>

                                </div>

                                <div className="d-flex flex-wrap mb-3">
                                    {profile.location && (
                                        <div className="me-4 d-flex align-items-center text-muted">
                                            <MapPin size={16} className="me-1"/>
                                            {profile.location}
                                        </div>
                                    )}
                                    {profile.years_experience && (
                                        <div className="me-4 d-flex align-items-center text-muted">
                                            <Calendar size={16} className="me-1"/>
                                            {profile.years_experience} years experience
                                        </div>
                                    )}
                                    {profile.genres && (
                                        <div className="d-flex align-items-center text-muted">
                                            <Music size={16} className="me-1"/>
                                            {profile.genres}
                                        </div>
                                    )}
                                </div>

                                {profile.bio && <p className="mb-3">{profile.bio}</p>}

                                {profile.social_links && profile.social_links.length > 0 && (
                                    <div className="d-flex mt-3">
                                        {profile.social_links.map((social, index) => (
                                            <a
                                                key={index}
                                                href={social.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="me-3 text-dark"
                                            >
                                                {renderSocialIcon(social.platform)}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Profile Content */}
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => k && setActiveTab(k)}
                    className="mb-4"
                >
                    <Tab eventKey="events" title="Playlist">
                        <Row className="g-4 py-3">
                            {profile.social_links?.some(link =>
                                ['soundcloud', 'spotify'].includes(link.platform.toLowerCase())
                            ) ? (
                                profile.social_links.map((link, index) => {
                                    const url = link.url;
                                    const platform = link.platform.toLowerCase();

                                    if (platform === 'soundcloud') {
                                        return (
                                            <Col key={`sc-${index}`} xs={12}>
                                                <div className="ratio" style={{height: 300}}>
                                                    <iframe
                                                        width="100%"
                                                        height="300"
                                                        scrolling="no"
                                                        frameBorder="no"
                                                        allow="autoplay"
                                                        src={`https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&color=%23ff5500&auto_play=false&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true&visual=true`}
                                                    ></iframe>
                                                </div>
                                                <div style={{
                                                    fontSize: '10px',
                                                    color: '#cccccc',
                                                    lineBreak: 'anywhere',
                                                    wordBreak: 'normal',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    textOverflow: 'ellipsis',
                                                    fontFamily: 'Interstate,Lucida Grande,Lucida Sans Unicode,Lucida Sans,Garuda,Verdana,Tahoma,sans-serif',
                                                    fontWeight: 100
                                                }}>
                                                    <a href={url.split('/').slice(0, 4).join('/')} target="_blank"
                                                       rel="noreferrer"
                                                       style={{color: '#cccccc', textDecoration: 'none'}}>
                                                        View Profile
                                                    </a>
                                                    {' '}·{' '}
                                                    <a href={url} target="_blank" rel="noreferrer"
                                                       style={{color: '#cccccc', textDecoration: 'none'}}>
                                                        Listen on SoundCloud
                                                    </a>
                                                </div>
                                            </Col>
                                        );
                                    }

                                    if (platform === 'spotify') {
                                        const spotifyEmbedUrl = url.replace('open.spotify.com', 'open.spotify.com/embed');
                                        return (
                                            <Col key={`sp-${index}`} xs={12}>
                                                <div className="ratio ratio-16x9">
                                                    <iframe
                                                        src={spotifyEmbedUrl}
                                                        width="100%"
                                                        height="380"
                                                        frameBorder="0"
                                                        allow="encrypted-media"
                                                        allowFullScreen
                                                    ></iframe>
                                                </div>
                                            </Col>
                                        );
                                    }

                                    return null;
                                })
                            ) :  (
                                <Col className="text-center py-5">
                                    <p className="text-muted">{t('djProfile.noEvents')}</p>
                                </Col>
                            )}
                        </Row>
                    </Tab>
                    <Tab eventKey="about" title={t('djProfile.tabs.about')}>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="py-4">
                                <h3 className="h4 mb-4">{t('djProfile.about.title')}</h3>

                                {profile.bio ? (
                                    <div className="mb-4">
                                        <h4 className="h5 mb-3">{t('djProfile.about.bio')}</h4>
                                        <p>{profile.bio}</p>
                                    </div>
                                ) : (
                                    <p className="text-muted">{t('djProfile.about.noBio')}</p>
                                )}

                                {profile.genres && (
                                    <div className="mb-4">
                                        <h4 className="h5 mb-3">{t('djProfile.about.genres')}</h4>
                                        <p>{profile.genres}</p>
                                    </div>
                                )}

                                {profile.years_experience && (
                                    <div className="mb-4">
                                        <h4 className="h5 mb-3">{t('djProfile.about.experience')}</h4>
                                        <p>{profile.years_experience} y</p>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Tab>
                </Tabs>
            </Container>
        </div>
    );
};

export default DjProfile;
